// Dawn-konformes Custom Analytics Tracking - Optimized & Minimal
// Erkennung primär über element_class für robuste Event-Zuordnung
(function () {
  'use strict';

  if (typeof Shopify === 'undefined' || !Shopify.analytics || !Shopify.analytics.publish) {
    console.error('[Custom Events] Shopify.analytics.publish nicht verfügbar');
    return;
  }

  // ============================================
  // UTILITIES
  // ============================================

  const throttle = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const getText = (selector) => document.querySelector(selector)?.textContent.trim() || '';
  const getPrice = (selector) => document.querySelector(selector)?.textContent.replace(/[^0-9.,]/g, '').trim() || '';

  /** Tiefe Kopie nur serialisierbarer Daten — vermeidet "readonly property" nach Shopify-freeze */
  const cloneSerializableForPublish = (obj) => {
    try {
      return JSON.parse(JSON.stringify(obj && typeof obj === 'object' ? obj : {}));
    } catch (e) {
      return {};
    }
  };

  const publishEvent = (eventName, eventData = {}) => {
    try {
      const merged = Object.assign(
        { timestamp: new Date().toISOString() },
        cloneSerializableForPublish(eventData)
      );
      Shopify.analytics.publish(eventName, Object.assign(merged, {
        context: {
          document: {
            location: {
              href: String(location.href),
              pathname: String(location.pathname),
              search: String(location.search)
            },
            referrer: String(document.referrer),
            title: String(document.title)
          },
          viewport: { width: innerWidth, height: innerHeight },
          device: { is_mobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) }
        }
      }));
    } catch (e) {
      console.error('[Custom Events] Fehler:', eventName, e);
    }
  };

  // ============================================
  // CONTEXT COLLECTORS (mit Caching)
  // ============================================

  const cache = {
    product: null,
    collection: null,
    utm: null,
    lastUrl: null,
    lastUpdate: 0,
    TTL: 5000
  };

  /** Nach publish können Payloads eingefroren sein — nie dieselbe Referenz an publish zurückgeben */
  const shallowCloneProductContext = (p) => {
    if (!p || typeof p !== 'object') return null;
    return {
      ...p,
      selected_options: p.selected_options && typeof p.selected_options === 'object'
        ? { ...p.selected_options }
        : {}
    };
  };

  const shallowCloneCollectionContext = (c) => {
    if (!c || typeof c !== 'object') return null;
    return {
      ...c,
      active_filters: Array.isArray(c.active_filters) ? [...c.active_filters] : []
    };
  };

  // Cache invalidieren bei Seitenwechsel
  let currentPath = location.pathname;

  // Jedes Mal ein neues Plain-Object: Shopify kann publish-Payloads einfrieren;
  // dieselbe utm-Referenz erneut zu übergeben führt auf Safari/iOS zu
  // "TypeError: Attempted to assign to readonly property."
  const getUTMParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
      has_utm: params.has('utm_source') || params.has('utm_medium')
    };
  };

  // Helper: Normalisiert Option-Name (entfernt numerischen Suffix)
  const normalizeOptionName = (name) => {
    if (!name) return '';
    const cleaned = name.replace(/options\[|\]/g, '').trim();
    return cleaned.includes('-') ? cleaned.split('-')[0].trim() : cleaned;
  };

  const getProductContext = (force = false) => {
    const now = Date.now();
    if (!force &&
      cache.product &&
      (now - cache.lastUpdate < cache.TTL) &&
      cache.lastUrl === location.pathname) {
      return shallowCloneProductContext(cache.product);
    }

    if (!document.querySelector('.product')) {
      cache.product = null;
      return null;
    }

    const selectedOptions = {};

    // Sammle alle Radio-Inputs und Swatch-Inputs (alle Variantenoptionen)
    document.querySelectorAll('input[type="radio"][name^="options"]:checked, .swatch-input__input:checked, input[type="radio"][name*="-"]:checked').forEach(input => {
      const name = normalizeOptionName(input.name);
      const label = document.querySelector(`label[for="${input.id}"]`);
      const value = label?.textContent.split('\n')[0].trim() || input.value.trim();
      if (name && value) {
        selectedOptions[name] = value;
      }
    });

    // Sammle alle Select-Elemente (Dropdown-Varianten)
    document.querySelectorAll('select[name^="options"], select[name*="-"]').forEach(select => {
      if (select.value) {
        const name = normalizeOptionName(select.name);
        const value = select.value.trim() || select.options[select.selectedIndex]?.text?.trim() || '';
        if (name && value) {
          selectedOptions[name] = value;
        }
      }
    });

    // Zusätzlich: Sammle aus variant-selects data-selected-value (falls vorhanden)
    document.querySelectorAll('variant-selects [data-selected-value]').forEach(el => {
      const legend = el.closest('fieldset')?.querySelector('legend');
      if (legend) {
        const name = normalizeOptionName(legend.textContent.split(':')[0].trim());
        const value = el.textContent.trim();
        if (name && value && !selectedOptions[name]) {
          selectedOptions[name] = value;
        }
      }
    });

    const variantInput = document.querySelector('input[name="id"]');
    const addToCartBtn = document.querySelector('.product-form__submit, [name="add"]');
    const btnText = addToCartBtn?.textContent.trim().toLowerCase() || '';

    const isAvailable = addToCartBtn &&
      !addToCartBtn.disabled &&
      !btnText.includes('ausverkauft') &&
      !btnText.includes('sold out') &&
      !btnText.includes('nicht vorrätig') &&
      !btnText.includes('unavailable');

    cache.product = {
      product_name: getText('.product__title, [itemprop="name"]'),
      product_vendor: getText('.product__vendor, [itemprop="brand"]'),
      product_type: getText('.product__type'),
      variant_id: variantInput?.value || '',
      quantity: document.querySelector('input[name="quantity"]')?.value || '1',
      price: getPrice('.price-item--regular .price-item__regular, .price__regular .price-item, .product__price'),
      compare_at_price: getPrice('.price-item--regular .price-item__compare, .price__compare-at .price-item'),
      currency: Shopify.currency?.active || 'EUR',
      selected_options: selectedOptions,
      options_count: Object.keys(selectedOptions).length,
      is_available: isAvailable,
      availability_text: getText('.product__inventory, .product-form__error-message'),
      sku: getText('.product__sku, [data-sku]'),
      current_image_url: document.querySelector('.product__media img.is-active, .product__media img')?.src || '',
      total_images: document.querySelectorAll('.product__media img, .product__media-item').length,
      rating: getText('[itemprop="ratingValue"], .product__rating'),
      review_count: getText('[itemprop="reviewCount"], .product__review-count'),
      has_discount: !!document.querySelector('.price-item--regular .price-item__compare')
    };

    cache.lastUpdate = now;
    cache.lastUrl = location.pathname;
    return shallowCloneProductContext(cache.product);
  };

  const getCollectionContext = () => {
    const now = Date.now();
    if (cache.collection && (now - cache.lastUpdate < cache.TTL) && cache.lastUrl === location.pathname) {
      return shallowCloneCollectionContext(cache.collection);
    }

    if (!document.querySelector('.collection, .search')) {
      cache.collection = null;
      return null;
    }

    cache.collection = {
      collection_name: getText('.collection__title, .search__title, h1'),
      total_products_visible: document.querySelectorAll('.card-wrapper, .product-card, .grid__item').length,
      active_filters: Array.from(document.querySelectorAll('.active-facets__button, [data-active-filter]')).map(el => el.textContent.trim()),
      sort_by: document.querySelector('select[name="sort_by"]')?.value || ''
    };

    return shallowCloneCollectionContext(cache.collection);
  };

  // ============================================
  // CLICK HANDLER (Erkennung primär über element_class)
  // ============================================

  document.addEventListener('click', (e) => {
    const clickedElement = e.target;
    let target = clickedElement;

    // Erweiterte Target-Erkennung
    if (clickedElement.matches('img, picture, video')) {
      target = clickedElement;
    } else {
      target = clickedElement.closest('a, button, input, label, [role="button"], .product__media-item, .quantity__button, .swatch-input__input, .deferred-media__poster, .product__media-toggle');
    }

    if (!target) return;

    const elementClass = target.className || '';
    const elementText = target.textContent?.trim().toLowerCase() || '';
    const ariaLabel = target.getAttribute('aria-label') || '';

    // Quick Add Button Erkennung (vor Early Exit)
    if (target.matches('button[name="add"][type="submit"]') &&
      (target.id.includes('quick-add') || target.closest('[id*="quick-add"]'))) {
      const productCard = target.closest('.card-wrapper, .product-card, [data-product-id]');
      const productId = productCard?.dataset?.productId ||
        target.id.match(/product-grid(\d+)/)?.[1] ||
        '';

      publishEvent('c_quick_add', {
        element_id: target.id || '',
        element_name: target.name || '',
        product_id: productId,
        product_context: productCard ? {
          product_name: productCard.querySelector('.card__heading, .product-card__title')?.textContent?.trim() || '',
          product_price: productCard.querySelector('.price, .product-card__price')?.textContent?.trim() || ''
        } : null,
        utm_params: getUTMParams()
      });
      // Shopify Standard-Event (product_added_to_cart) wird trotzdem ausgelöst
      return;
    }

    // Early Exit: Skip Navigation und Shopify Standard Events
    if (elementClass.includes('header__icon') ||
      elementClass.includes('header__menu-item') ||
      elementClass.includes('product-media-modal__toggle') || // Modal Toggle nicht tracken
      target.matches('[name="add"], .product-form__submit, [data-add-to-cart], a[href*="/products/"], a[href*="/collections/"], [href*="/checkout"], .cart__checkout-button, [type="search"], .search__button, [data-search]')) {
      return; // Shopify Standard Events oder Navigation
    }

    const baseData = {
      element_type: target.tagName.toLowerCase(),
      element_class: elementClass,
      element_id: target.id || '',
      element_text: target.textContent?.trim().substring(0, 100) || '',
      element_href: target.href || '',
      click_position: { x: e.clientX, y: e.clientY },
      utm_params: getUTMParams()
    };

    let eventType = null;
    let shouldPublish = false;

    // ERKENNUNG PRIMÄR ÜBER element_class (spezifische Events, keine generischen)
    // Varianten- und Quantity-Tracking erfolgt über separate Event-Listener (siehe unten)
    // 1. Video Poster/Thumbnail (deferred-media__poster ODER thumbnail mit Video aria-label)
    if (elementClass.includes('deferred-media__poster') ||
      (elementClass.includes('thumbnail') && (ariaLabel.toLowerCase().includes('video') || ariaLabel.toLowerCase().includes('abspielen')))) {
      eventType = 'c_video_start';
      shouldPublish = true;
      const video = target.closest('.deferred-media')?.querySelector('video') ||
        document.querySelector('video');
      baseData.video_url = video?.currentSrc || video?.src || '';
      baseData.video_index = Array.from(document.querySelectorAll('video')).indexOf(video);
      baseData.product_context = getProductContext();
    }
    // 2. Produktbild/Media (ERWEITERT - auch thumbnail Buttons für Bilder)
    else if (elementClass.includes('product__media-toggle') ||
      elementClass.includes('product__media-zoom-lightbox') ||
      elementClass.includes('thumbnail') || // Thumbnail Buttons (Bilder)
      (clickedElement.matches('img') && clickedElement.closest('.product')) ||
      (target.matches('img') && target.closest('.product')) ||
      target.matches('.product__media img, .product__media-item img, .product img')) {
      eventType = 'c_view_product_image';
      shouldPublish = true;

      // Finde das tatsächliche Bild-Element (robust)
      let imgElement = null;
      if (clickedElement.matches('img')) {
        imgElement = clickedElement;
      } else if (target.matches('img')) {
        imgElement = target;
      } else {
        imgElement = target.querySelector('img') ||
          clickedElement.closest('img') ||
          clickedElement.querySelector('img');
      }

      baseData.media_id = imgElement?.dataset?.mediaId || target.dataset?.mediaId || '';
      // WICHTIG: currentSrc zuerst (tatsächlich geladene URL bei srcset), dann src (Fallback)
      baseData.image_url = imgElement?.currentSrc || imgElement?.src || imgElement?.getAttribute('data-src') || target.src || '';
      baseData.image_alt = imgElement?.alt || target.alt || '';
      baseData.product_context = getProductContext();
    }
    // 3. Filter/Facets
    else if (elementClass.includes('facets__button') ||
      elementClass.includes('active-facets__button') ||
      target.matches('.facets__button, [data-facet], .active-facets__button')) {
      eventType = 'c_filter_click';
      shouldPublish = true;
      baseData.filter_value = target.textContent?.trim() || '';
      baseData.filter_action = elementClass.includes('active-facets__button') ? 'remove' : 'add';
      baseData.collection_context = getCollectionContext();
    }

    // Nur publizieren wenn spezifisches Event erkannt wurde (keine generischen custom_click_event)
    if (shouldPublish && eventType) {
      publishEvent(eventType, baseData);
    }
  }, { passive: true });

  // ============================================
  // VARIANTEN-TRACKING (Dawn-native Events)
  // ============================================

  // Globaler State für vorherige Werte (wird beim Laden initialisiert und nach jedem Event aktualisiert)
  const variantState = {
    previousOptions: {}
  };

  // Initialisiere previousOptions beim Laden
  const initVariantState = () => {
    cache.product = null;
    cache.lastUpdate = 0;
    const productCtx = getProductContext();
    if (productCtx?.selected_options) {
      variantState.previousOptions = { ...productCtx.selected_options };
    }
  };

  // Initialisiere beim Laden
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantState);
  } else {
    initVariantState();
  }

  // Helper: Extrahiert neuen Wert aus Input
  const extractNewValue = (input) => {
    if (input.type === 'radio' && input.checked) {
      const label = document.querySelector(`label[for="${input.id}"]`);
      return label?.textContent?.split('\n')[0].trim() || input.value?.trim() || '';
    }
    if (input.tagName === 'SELECT') {
      return input.value?.trim() || input.options[input.selectedIndex]?.text?.trim() || '';
    }
    return input.value?.trim() || '';
  };

  // Helper: Findet Variante aus DOM oder Event-Daten
  const getVariantData = (variantSelects, eventData) => {
    // Versuche zuerst aus Dawn's Pub/Sub Event
    if (eventData?.data?.variant) return eventData.data.variant;

    // Fallback: aus DOM
    const variantDataEl = variantSelects?.querySelector('[data-selected-variant]');
    const variantJson = variantDataEl?.textContent || variantDataEl?.dataset?.selectedVariant;
    if (variantJson) {
      try {
        return JSON.parse(variantJson);
      } catch (e) { }
    }
    return null;
  };

  // Helper: Publiziert Varianten-Änderungs-Event
  const publishVariantChangeEvent = (input, optionName, optionNameForLookup, previousValue, newValue, variantSelects, eventData) => {
    cache.product = null;
    cache.lastUpdate = 0;
    const productCtx = getProductContext(true);
    if (!productCtx && !document.querySelector('.product')) return;

    const variant = getVariantData(variantSelects, eventData);

    publishEvent('c_change_product_variant', {
      element_type: input.tagName.toLowerCase(),
      element_class: input.className || '',
      element_id: input.id || '',
      variant_change: {
        option_name: optionNameForLookup || optionName,
        previous_value: previousValue,
        new_value: newValue
      },
      product_context: productCtx,
      variant_id: variant?.id || null,
      variant_price: variant?.price ? variant.price / 100 : null,
      variant_available: variant?.available || false,
      utm_params: getUTMParams()
    });

    if (productCtx?.selected_options) {
      variantState.previousOptions = { ...productCtx.selected_options };
    }
  };

  // Handler für Varianten-Änderungen
  const handleVariantChange = (e) => {
    const input = e.target;
    if (!input || input.name === 'quantity') return;

    const variantSelects = input.closest('variant-selects');
    if (!variantSelects || !input.matches('input[type="radio"][name*="-"], input[name^="options"], select[name*="-"], .swatch-input__input')) return;

    const optionName = normalizeOptionName(input.name);
    const optionNameForLookup = optionName;
    const previousValue = variantState.previousOptions[optionNameForLookup] || variantState.previousOptions[optionName] || '';
    const newValue = extractNewValue(input);

    let unsubscribe = null;
    let fallbackTimeout = null;

    const processVariantChange = (eventData) => {
      if (unsubscribe) unsubscribe();
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
      publishVariantChangeEvent(input, optionName, optionNameForLookup, previousValue, newValue, variantSelects, eventData);
    };

    // Versuche Dawn's Pub/Sub System zu nutzen
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, processVariantChange);
    }

    // Fallback: Timeout falls Pub/Sub nicht verfügbar
    if (!unsubscribe) {
      fallbackTimeout = setTimeout(() => processVariantChange(null), 300);
    }
  };

  // Event-Listener für alle vorhandenen variant-selects
  const setupVariantTracking = () => {
    document.querySelectorAll('variant-selects').forEach(variantSelects => {
      if (!variantSelects.hasAttribute('data-variant-tracked')) {
        variantSelects.addEventListener('change', handleVariantChange);
        variantSelects.setAttribute('data-variant-tracked', 'true');
      }
    });
  };

  // Initial Setup
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupVariantTracking);
  } else {
    setupVariantTracking();
  }


  // ============================================
  // QUANTITY-TRACKING (Standard Change Event)
  // ============================================

  // Initialisiere data-prev beim Laden
  const initQuantityTracking = () => {
    document.querySelectorAll('input[name="quantity"]').forEach(input => {
      input.setAttribute('data-prev', input.value || '1');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuantityTracking);
  } else {
    initQuantityTracking();
  }

  // Change Event für Quantity
  document.addEventListener('change', (e) => {
    if (e.target.name === 'quantity') {
      const input = e.target;
      const prev = parseInt(input.getAttribute('data-prev') || '1');
      const curr = parseInt(input.value || '1');

      if (curr === prev) return; // Keine Änderung

      publishEvent('c_quantity_change', {
        element_type: 'input',
        element_class: input.className || '',
        element_id: input.id || '',
        quantity_change: {
          action: curr > prev ? 'increase' : 'decrease',
          previous_quantity: prev,
          new_quantity: curr
        },
        product_context: getProductContext(true), // Force refresh für aktuelle Quantity
        utm_params: getUTMParams()
      });

      // Update data-prev
      input.setAttribute('data-prev', curr);
    }
  }, { passive: true });

  // ============================================
  // KOMBINIERTER MUTATION OBSERVER (Performance-Optimierung)
  // ============================================

  const combinedObserver = new MutationObserver((mutations) => {
    let urlChanged = false;
    const addedNodes = [];

    mutations.forEach((mutation) => {
      // URL-Änderung prüfen
      if (location.pathname !== currentPath) {
        urlChanged = true;
        currentPath = location.pathname;
      }

      // Neue Nodes sammeln
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          addedNodes.push(node);
        }
      });
    });

    // URL-Änderung: Cache und variantState zurücksetzen
    if (urlChanged) {
      cache.product = null;
      cache.collection = null;
      cache.lastUrl = null;
      cache.lastUpdate = 0;
      setTimeout(initVariantState, 100);
    }

    // Neue Nodes verarbeiten
    addedNodes.forEach((node) => {
      // Variant-selects
      if (node.matches && node.matches('variant-selects')) {
        if (!node.hasAttribute('data-variant-tracked')) {
          node.addEventListener('change', handleVariantChange);
          node.setAttribute('data-variant-tracked', 'true');
        }
      }

      // Variant-selects innerhalb von node
      const variantSelects = node.querySelectorAll && node.querySelectorAll('variant-selects');
      if (variantSelects) {
        variantSelects.forEach(vs => {
          if (!vs.hasAttribute('data-variant-tracked')) {
            vs.addEventListener('change', handleVariantChange);
            vs.setAttribute('data-variant-tracked', 'true');
          }
        });
      }

      // Quantity-Inputs
      const quantityInputs = node.matches && node.matches('input[name="quantity"]')
        ? [node]
        : (node.querySelectorAll && node.querySelectorAll('input[name="quantity"]') || []);

      quantityInputs.forEach(input => {
        if (!input.hasAttribute('data-prev')) {
          input.setAttribute('data-prev', input.value || '1');
        }
      });
    });
  });

  // Einziger Observer für alles
  if (document.body) {
    combinedObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      combinedObserver.observe(document.body, { childList: true, subtree: true });
    }, { once: true });
  }

  // ============================================
  // ERROR & PERFORMANCE TRACKING
  // ============================================

  // Global Error Handler (c_error_occurred)
  const errorRateLimit = 10; // Max 10 pro Minute
  let errorTimestamps = [];

  window.addEventListener('error', (e) => {
    if (!e.error) return; // Nur JavaScript Errors

    const now = Date.now();
    errorTimestamps.push(now);
    errorTimestamps = errorTimestamps.filter(ts => now - ts < 60000);

    if (errorTimestamps.length <= errorRateLimit) {
      if (window.requestIdleCallback) {
        requestIdleCallback(() => {
          publishEvent('c_error_occurred', {
            error_message: e.message?.substring(0, 200) || '',
            error_file: e.filename || '',
            error_line: e.lineno || 0,
            utm_params: getUTMParams()
          });
        });
      } else {
        setTimeout(() => {
          publishEvent('c_error_occurred', {
            error_message: e.message?.substring(0, 200) || '',
            error_file: e.filename || '',
            error_line: e.lineno || 0,
            utm_params: getUTMParams()
          });
        }, 0);
      }
    }
  }, { passive: true });

  // Slow Page Load (c_slow_page_load)
  if (window.performance?.timing) {
    const checkLoadTime = () => {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      if (loadTime > 3000) {
        if (window.requestIdleCallback) {
          requestIdleCallback(() => {
            publishEvent('c_slow_page_load', {
              load_time_ms: loadTime,
              performance_metrics: {
                dns: performance.timing.domainLookupEnd - performance.timing.domainLookupStart,
                connect: performance.timing.connectEnd - performance.timing.connectStart,
                dom: performance.timing.domContentLoadedEventEnd - performance.timing.domLoading
              },
              utm_params: getUTMParams()
            });
          });
        } else {
          setTimeout(() => {
            publishEvent('c_slow_page_load', {
              load_time_ms: loadTime,
              performance_metrics: {
                dns: performance.timing.domainLookupEnd - performance.timing.domainLookupStart,
                connect: performance.timing.connectEnd - performance.timing.connectStart,
                dom: performance.timing.domContentLoadedEventEnd - performance.timing.domLoading
              },
              utm_params: getUTMParams()
            });
          }, 0);
        }
      }
    };

    if (document.readyState === 'complete') {
      checkLoadTime();
    } else {
      window.addEventListener('load', checkLoadTime, { once: true, passive: true });
    }
  }

  // Image Load Failed (c_image_load_failed)
  let imageErrorCount = 0;
  document.addEventListener('error', (e) => {
    if (e.target.tagName === 'IMG' && !e.target.complete && imageErrorCount < 5) {
      imageErrorCount++;
      if (window.requestIdleCallback) {
        requestIdleCallback(() => {
          publishEvent('c_image_load_failed', {
            image_src: e.target.src?.substring(0, 500) || '',
            image_alt: e.target.alt || '',
            product_context: getProductContext(),
            utm_params: getUTMParams()
          });
        });
      } else {
        setTimeout(() => {
          publishEvent('c_image_load_failed', {
            image_src: e.target.src?.substring(0, 500) || '',
            image_alt: e.target.alt || '',
            product_context: getProductContext(),
            utm_params: getUTMParams()
          });
        }, 0);
      }
    }
  }, true); // Capture Phase für bessere Erfassung

  // ============================================
  // FORM SUBMIT (nur Custom Forms)
  // ============================================

  document.addEventListener('submit', throttle((e) => {
    const form = e.target;
    // Skip alle Shopify Forms (werden bereits getrackt)
    if (form.action.includes('/cart') ||
      form.action.includes('/checkout') ||
      form.action.includes('/search') ||
      form.closest('.search')) {
      return;
    }

    const formData = new FormData(form);
    const data = {};
    for (let [key, value] of formData.entries()) {
      if (!key.includes('password') && !key.includes('credit') && !key.includes('card')) {
        data[key] = value.substring(0, 100);
      }
    }

    publishEvent('c_form_interaction', {
      form_action: form.action || '',
      form_id: form.id || '',
      form_type: form.querySelector('[type="email"]') ? 'email' : 'other',
      form_data: data,
      utm_params: getUTMParams()
    });
  }, 500), { passive: true });

  // ============================================
  // SCROLL (nur für Session-Tracking, nicht publiziert)
  // ============================================

  let sessionStart = Date.now();
  let interactions = 0;
  let maxScroll = 0;

  window.addEventListener('scroll', (() => {
    let rafId = null;
    return () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const scrollPercent = Math.round((scrollY / (document.documentElement.scrollHeight - innerHeight)) * 100);
        maxScroll = Math.max(maxScroll, scrollPercent);
        rafId = null;
      });
    };
  })(), { passive: true });

  // ============================================
  // SESSION END / RESUME (Desktop + Mobile / iOS)
  // - c_session_end: echtes Verlassen (pagehide) oder Tab sichtbar aber inaktiv (Timer)
  // - c_session_resume: wieder sichtbar nach ≥20s Hintergrund (kein separater „hidden_timeout“-Ende mehr:
  //   iOS feuert den 20s-Timer oft beim Aufwachen parallel zu visible → doppeltes Signal)
  // - pagehide (ohne bfcache): Navigation / Tab zu / In-App-Browser zu
  // - pageshow (persisted): Zurück aus BFCache
  //
  // Performance: nur Listener-Registrierung + leichte Objektbildung; kein zusätzlicher Netzwerk-Traffic
  // beim Laden. session_diag wird erst bei End/Resume berechnet (kein Polling).
  // ============================================

  ['click', 'scroll', 'keypress'].forEach(type => {
    document.addEventListener(type, () => interactions++, { passive: true });
  });

  const BACKGROUND_END_MS = 20000;
  let sessionEndSent = false;
  let inactivityTimer = null;
  /** Zeitpunkt des letzten Wechsels zu visibility hidden (für iOS: Timer feuert oft erst beim Resume) */
  let hiddenAtMs = null;
  /** Page Lifecycle API (Chrome u.a.): Seite eingefroren */
  let hadPageFreeze = false;

  const getSessionDiagnostics = () => {
    let navigationType = '';
    try {
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) navigationType = nav.type;
    } catch (e) { /* ignore */ }

    let connection = null;
    try {
      const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (c) {
        connection = {
          effectiveType: c.effectiveType || '',
          saveData: !!c.saveData,
          downlink: typeof c.downlink === 'number' ? c.downlink : undefined
        };
      }
    } catch (e) { /* ignore */ }

    let standalone = false;
    try {
      standalone = !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
    } catch (e) { /* ignore */ }

    return {
      visibility_state: typeof document !== 'undefined' ? (document.visibilityState || '') : '',
      document_hidden: typeof document !== 'undefined' ? !!document.hidden : false,
      navigation_type: navigationType,
      connection,
      standalone_pwa: standalone,
      is_mobile_ua: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || ''),
      timezone_offset_minutes: new Date().getTimezoneOffset(),
      viewport: { width: typeof innerWidth === 'number' ? innerWidth : 0, height: typeof innerHeight === 'number' ? innerHeight : 0 },
      had_page_freeze: hadPageFreeze
    };
  };

  const sessionEndPayloadBase = () => {
    const duration = Math.round((Date.now() - sessionStart) / 1000);
    const diag = getSessionDiagnostics();
    return {
      session_duration_seconds: duration,
      total_interactions: interactions,
      max_scroll_depth: maxScroll,
      engagement_level: interactions > 10 ? 'high' : interactions > 3 ? 'medium' : 'low',
      page_type: document.querySelector('.product') ? 'product' :
        document.querySelector('.collection') ? 'collection' :
          document.querySelector('.cart') ? 'cart' : 'other',
      product_context: getProductContext(),
      utm_params: getUTMParams(),
      session_diag: diag
    };
  };

  /**
   * @param {object} meta z.B. { end_reason, hidden_ms, pagehide_persisted }
   * @param {{ force?: boolean }} opts pagehide: force auch bei kurzer Verweildauer
   */
  const publishSessionEnd = (meta = {}, opts = {}) => {
    if (sessionEndSent && !opts.force) return;

    const duration = Math.round((Date.now() - sessionStart) / 1000);
    const minDuration = opts.force ? 0 : 3;
    if (duration < minDuration) return;

    sessionEndSent = true;
    publishEvent('c_session_end', {
      ...sessionEndPayloadBase(),
      end_reason: meta.end_reason || 'unknown',
      hidden_ms: typeof meta.hidden_ms === 'number' ? meta.hidden_ms : undefined,
      pagehide_persisted: typeof meta.pagehide_persisted === 'boolean' ? meta.pagehide_persisted : undefined
    });
    hadPageFreeze = false;
  };

  const publishSessionResume = (meta = {}) => {
    const diag = getSessionDiagnostics();
    // sessionStart wird beim Verstecken nicht zurückgesetzt → diese Sekunden = Wanduhr seit
    // Aufruf/letztem Resume bis zum Resume, **inkl.** Hintergrund (nicht „nur aktiv“).
    publishEvent('c_session_resume', {
      resume_reason: meta.resume_reason || 'visible',
      hidden_ms: typeof meta.hidden_ms === 'number' ? meta.hidden_ms : undefined,
      session_duration_seconds_before_resume: Math.round((Date.now() - sessionStart) / 1000),
      utm_params: getUTMParams(),
      session_diag: diag
    });
    hadPageFreeze = false;
    sessionEndSent = false;
    sessionStart = Date.now();
  };

  const clearInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
  };

  // Inaktivitäts-Timer: nur sinnvoll, solange der Tab sichtbar ist (bei hidden pausieren)
  const resetInactivityTimer = () => {
    clearInactivityTimer();
    inactivityTimer = setTimeout(() => {
      if (document.visibilityState !== 'hidden' && !sessionEndSent) {
        publishSessionEnd({ end_reason: 'inactivity_visible', hidden_ms: 0 });
      }
    }, 60000); // 60 Sekunden (1 Minute)
  };

  // Initialisiere Inaktivitäts-Timer
  resetInactivityTimer();

  // Bei allen Interaktionen Timer zurücksetzen
  ['click', 'scroll', 'keypress', 'mousemove', 'touchstart'].forEach(type => {
    document.addEventListener(type, () => {
      interactions++;
      resetInactivityTimer();
    }, { passive: true });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAtMs = Date.now();
      clearInactivityTimer();
    } else {
      const now = Date.now();
      const hiddenMs = hiddenAtMs !== null ? now - hiddenAtMs : 0;
      hiddenAtMs = null;

      // ≥20 s unsichtbar: nur Resume (Dauer in hidden_ms). Kein c_session_end „hidden_timeout“ mehr
      // während hidden — vermeidet Race mit iOS (Timer + visible praktisch gleichzeitig).
      if (hiddenMs >= BACKGROUND_END_MS) {
        publishSessionResume({
          resume_reason: 'visible_after_background',
          hidden_ms: hiddenMs
        });
      }

      resetInactivityTimer();
    }
  }, { passive: true });

  // Echtes Verlassen: anderer Tab, Safari zu, Navigation, viele In-App-Browser (nicht BFCache)
  window.addEventListener('pagehide', (ev) => {
    if (ev.persisted) {
      return;
    }
    clearInactivityTimer();
    publishSessionEnd(
      { end_reason: 'pagehide', pagehide_persisted: false, hidden_ms: 0 },
      { force: true }
    );
  }, { capture: true });

  // Zurück aus BFCache (Safari Swipe-Back o.ä.)
  window.addEventListener('pageshow', (ev) => {
    if (!ev.persisted) return;
    publishSessionResume({ resume_reason: 'bfcache_restore' });
    resetInactivityTimer();
  }, { passive: true });

  if (typeof document !== 'undefined' && 'onfreeze' in document) {
    document.addEventListener('freeze', () => {
      hadPageFreeze = true;
    }, { passive: true });
  }

})();
